import { useState, useCallback } from 'react';
import type { FormApi } from 'final-form';

export type ChecksTabConfig = Record<string, string[][]>;

/** Minimal subset of the react-final-form FormApi used by this hook. */
export type AccordionTabFormApi = Pick<
  FormApi<Record<string, any>>,
  'getState' | 'change' | 'focus' | 'blur'
>;

type AccordionTabParams = {
  updateStatus: (item: number, status: string) => void;
  tabStatus: string[];
  pageType: string;
  checksTab: ChecksTabConfig;
};

function hasTabError(form: AccordionTabFormApi, tabFields: string[]): boolean {
  const state = form.getState();
  const errors = state.errors ?? {};
  const setPagesEmpty =
    tabFields.includes('set_pages') &&
    (!state.values.set_pages || Object.keys(state.values.set_pages).length === 0);
  const hasOfflineInstructionError = tabFields
    .filter(f => f.startsWith('offline_instructions_'))
    .some(field => {
      if (!state.values[`${field}_enabled`]) return false;
      const sections: { label: string; value: string }[] = state.values[field] ?? [];
      return sections.some(
        s => (s.label?.trim() && !s.value?.trim()) || (!s.label?.trim() && s.value?.trim())
      );
    });
  return (
    tabFields.some(field => Object.keys(errors).includes(field)) ||
    (tabFields[0] === 'rows' && !!state.values.rows?.some(row => row.quantity === 0)) ||
    setPagesEmpty ||
    hasOfflineInstructionError
  );
}

function triggerValidation(form: AccordionTabFormApi, validateFields: string[]) {
  const formState = form.getState();
  validateFields.forEach((fieldName: string) => {
    if (fieldName.startsWith('offline_instructions_') && !fieldName.endsWith('_enabled')) {
      if (!formState.values[`${fieldName}_enabled`]) return;
      const sections: { label: string; value: string }[] = formState.values[fieldName] ?? [];
      sections.forEach((_, index) => {
        [`${fieldName}[${index}].label`, `${fieldName}[${index}].value`].forEach(path => {
          form.focus(path);
          form.blur(path);
        });
      });
      return;
    }
    const value = formState.values[fieldName];
    const isEmpty =
      !value ||
      (fieldName === 'set_pages' &&
        (typeof value !== 'object' || Object.keys(value || {}).length === 0));
    if (isEmpty) {
      if (fieldName === 'description') {
        form.change(fieldName, '<br />');
      } else if (fieldName !== 'set_pages') {
        form.change(fieldName, value ?? '');
      }
      form.focus(fieldName);
      form.blur(fieldName);
    }
  });
}

export type AccordionTabHandleClick = (item: number, form: any, type?: 'tab' | 'next') => void;

export function useAccordionTab({
  updateStatus,
  tabStatus,
  pageType,
  checksTab,
}: AccordionTabParams) {
  const [tabIndexVisible, setTabIndexVisible] = useState(0);

  const handleClickAccordion: AccordionTabHandleClick = useCallback(
    (item, form, type) => {
      // Capture the tab being left before toggling visibility.
      const leaving = tabIndexVisible;
      setTabIndexVisible(prev => (prev === item ? -1 : item));

      const tabConfig = checksTab[pageType];
      if (!tabConfig) return;
      const formApi = form as AccordionTabFormApi;

      // Validate a tab's fields first, then read the fresh errors — to avoid stale validation cycles
      const evaluate = (tabIdx: number) => {
        const fields = tabConfig[tabIdx] ?? [];
        triggerValidation(formApi, fields);
        updateStatus(tabIdx, hasTabError(formApi, fields) ? 'error' : 'success');
      };

      if (type === 'tab') {
        if (tabStatus[item] === '') {
          triggerValidation(formApi, tabConfig[item] ?? []);
          updateStatus(item, 'in-progress');
        } else {
          evaluate(item);
        }
      } else if (type === 'next') {
        // Validate the tab being left, not item - 1: Next can skip tabs (non-super users jump past Donation Options), so item - 1 may point to the wrong tab.
        if (leaving >= 0 && leaving !== item) evaluate(leaving);
        if (tabStatus[item] === '') {
          updateStatus(item, 'in-progress');
        } else {
          updateStatus(item, hasTabError(formApi, tabConfig[item] ?? []) ? 'error' : 'success');
        }
      }
    },
    [updateStatus, tabStatus, tabIndexVisible, pageType, checksTab]
  );

  return { handleClickAccordion, tabIndexVisible, setTabIndexVisible };
}
